// 云打印
import type * as printing from "./types/printing"
import { REPO_MOCK_SERVER_URL, request } from "@/utils/service"

const PREFIX = "teashop_merchant/"

export function getLocalPrintPriority() {
  return request<IApiResponseData<printing.LocalPrintPriorityResponse>>({
    url: `${PREFIX}merchant_print_task/local/priority`,
    method: "GET",
    skipGlobalErrorToast: true,
    skipAuthExpiredPrompt: true,
  })
}

export function heartbeatLocalPrintDevice(data: printing.LocalPrintDeviceRequest) {
  return request<IApiResponseData<printing.LocalPrintPriorityResponse>>({
    url: `${PREFIX}merchant_print_task/local/priority/heartbeat`,
    method: "POST",
    data,
    skipGlobalErrorToast: true,
  })
}

export function reportLocalPrintPresence(data: printing.LocalPrintDeviceRequest) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_print_task/local/priority/presence`,
    method: "POST",
    data,
    skipGlobalErrorToast: true,
  })
}

export function claimLocalPrintBatch(data: printing.LocalPrintBatchRequest) {
  return request<IApiResponseData<printing.LocalPrintBatchResponse>>({
    url: `${PREFIX}merchant_print_task/local/claim/v4/batch`,
    method: "POST",
    data,
    skipGlobalErrorToast: true,
  })
}

export function setLocalPrintPriority(data: printing.LocalPrintPriorityRequest) {
  return request<IApiResponseData<printing.LocalPrintPriorityResponse>>({
    url: `${PREFIX}merchant_print_task/local/priority`,
    method: "PUT",
    data,
    skipGlobalErrorToast: true,
  })
}

export function claimLocalPrintTasksV4(data: printing.LocalPrintDeviceRequest, scene: printing.PrintScene) {
  return request<IApiResponseData<printing.LocalPrintTaskVO[]>>({
    url: `${PREFIX}merchant_print_task/local/claim/v4`,
    method: "POST",
    params: { scene },
    data,
    skipGlobalErrorToast: true,
  })
}

export type KitchenOrderPrintStatus = import("@repo/api-types/merchant").components["schemas"]["KitchenOrderPrintStatusResponse"]

export function getKitchenOrderPrintStatus(merchantTableRecordId: string | number, requestId: string) {
  return request<IApiResponseData<KitchenOrderPrintStatus>>({
    url: `${PREFIX}merchant_print_task/kitchen-orders/status`,
    method: "GET",
    params: { merchantTableRecordId, requestId },
  })
}
export const DEVICE_MANAGE_PRINTER_API_MOCK = false

// 获取打印机列表
export function getPrinterList() {
  return request<IApiPageResponseData<printing.MerchantPrintInfoRelVO>>({
    url: `${PREFIX}merchant_oms_print_info_rel`,
    method: "GET",
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

// 解绑设备
export function unbindPrintDevice(relIdList: number[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel/${relIdList}`,
    method: "DELETE",
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

// 打印机绑定商家
export function printerBindMerchant(data: printing.BindMerchantsRequest[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel`,
    method: "POST",
    data,
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

// 修改云打印机打印设置
export function updateCloudPrinterSetting(data: printing.PrintSettingDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel`,
    method: "PUT",
    data,
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

// 设置默认云打印机
export function setDefaultPrintDevice(relId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel/default/${relId}`,
    method: "PATCH",
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

// 打印已结账订单
export function printCheckedBill(data: printing.PayOrderPrintContent, printerRelId?: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel/print-order`,
    method: "POST",
    data: {
      ...data,
      printerRelId,
    },
  })
}

/**
 * 打印会员充值单
 * @param data 包含 chargeRecordId 的对象
 * @param data.chargeRecordId 收费记录 id
 * @returns Promise<IApiResponseData<boolean>>
 */
export function printRechargeBill(data: { chargeRecordId: number }) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel/print-recharge`,
    method: "POST",
    params: data,
  })
}

export function getPrintRuleConfig(scene: printing.PrintScene = "KITCHEN_ORDER") {
  return request<IApiResponseData<printing.MerchantPrintRuleConfigVO>>({
    url: `${PREFIX}merchant_print_rule`,
    method: "GET",
    params: { scene },
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

export function savePrintRules(data: printing.MerchantPrintRuleBatchSaveDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_print_rule/batch_save`,
    method: "POST",
    data,
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

export function getPendingLocalPrintTasks(scene: printing.PrintScene = "KITCHEN_ORDER", limit = 20) {
  return request<IApiResponseData<printing.LocalPrintTaskVO[]>>({
    url: `${PREFIX}merchant_print_task/local/pending`,
    method: "GET",
    params: { scene, limit },
  })
}

export function claimLocalPrintTasks(scene: printing.PrintScene = "KITCHEN_ORDER", limit = 20, clientId?: string) {
  return request<IApiResponseData<printing.LocalPrintTaskVO[]>>({
    url: `${PREFIX}merchant_print_task/local/claim`,
    method: "POST",
    params: { scene, limit, clientId },
  })
}

export function getLocalPrintCapabilities() {
  return request<IApiResponseData<printing.LocalPrintCapabilityVO>>({
    url: `${PREFIX}merchant_print_task/local/capabilities`,
    method: "GET",
  })
}

export function claimLocalPrintTasksV2(
  data: printing.ClaimLocalPrintTasksV2DTO,
  scene: printing.PrintScene = "KITCHEN_ORDER",
  limit = 20,
  clientId?: string,
) {
  return request<IApiResponseData<printing.LocalPrintTaskVO[]>>({
    url: `${PREFIX}merchant_print_task/local/claim/v2`,
    method: "POST",
    params: { scene, limit, clientId },
    data,
  })
}

export function claimLocalPrintTasksV3(
  data: printing.ClaimLocalPrintTasksV3DTO,
  scene: printing.PrintScene = "KITCHEN_ORDER",
  limit = 1,
) {
  return request<IApiResponseData<printing.ClaimLocalPrintTasksV3Response>>({
    url: `${PREFIX}merchant_print_task/local/claim/v3`,
    method: "POST",
    params: { scene, limit },
    data,
  })
}

export function completeLocalPrintDelivery(
  deliveryId: number,
  data: printing.CompleteLocalPrintDeliveryDTO,
) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_print_task/local/deliveries/${deliveryId}/complete`,
    method: "POST",
    data,
  })
}

export function completeLocalPrintTask(taskId: number, data: printing.CompleteLocalPrintTaskDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_print_task/local/${taskId}/complete`,
    method: "POST",
    data,
  })
}

export interface ManualKitchenOrderPrintRequest {
  requestId: string
  merchantTableRecordId: string | number
  items: Array<{
    merchantOmsItemOrderId: string | number
    itemLineId: string
  }>
}

export interface ManualKitchenOrderPrintResponse {
  status: "SUCCESS" | "PARTIAL_SUCCESS" | "NO_PRINT_RULE" | "FAILED"
  requestedItemLineCount: number
  acceptedItemLineCount: number
  successTaskCount: number
  pendingLocalTaskCount: number
  failedTaskCount: number
  noRuleItemLines: string[]
}

/** 按后端权威商品行创建真实厨房打印任务。 */
export function printKitchenOrderItems(data: ManualKitchenOrderPrintRequest) {
  return request<IApiResponseData<ManualKitchenOrderPrintResponse>>({
    url: `${PREFIX}merchant_print_task/kitchen-orders`,
    method: "POST",
    data,
  })
}

export function testPrint(relId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_print_rule/test_print/${relId}`,
    method: "POST",
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

export function testVoice(relId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_print_rule/test_voice/${relId}`,
    method: "POST",
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}
